import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { asyncScheduler, NEVER, Observable, throwError, timer } from 'rxjs';
import { catchError, map, observeOn, retry, share } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { environment } from '../../../environments/environment';
import { LocalStorage } from '../../storage.class';
import { ApiErrorResponse } from '../api/api.service';
import { ConfirmDialogService } from '../confirm-dialog-service/confirm-dialog.service';

type WebsocketMessage<T> = {
    type: string;
    data: T;
    success: boolean;
    reinit: boolean;
};

@Injectable({ providedIn: 'root' })
export class WebsocketService {
    websocketOpen = new EventEmitter<boolean>();

    private websocket: WebSocketSubject<unknown> | null = null;
    private sharedWebsocket: Observable<WebsocketMessage<unknown>> | null = null;

    private confirmDialogService = inject(ConfirmDialogService);
    private dialog = inject(MatDialog);

    public disconnect() {
        this.websocket?.unsubscribe();

        this.websocket = null;
        this.sharedWebsocket = null;
    }

    public getWebsocket<T>(type: string) {
        return this.getSharedWebsocket().pipe(
            map((data: WebsocketMessage<unknown> | undefined) =>
                data && data.type === type ? (data as WebsocketMessage<T>) : undefined
            )
        );
    }

    // Single retry/backoff/auth/error pipeline shared by every RealtimeService subscriber,
    // so one dropped connection or one auth failure is only logged/handled once, not once per entity type.
    private getSharedWebsocket() {
        if (!this.sharedWebsocket) {
            this.sharedWebsocket = this.getFullWebsocket().pipe(
                observeOn(asyncScheduler),
                map((data: WebsocketMessage<unknown>) => {
                    if (typeof data.data !== 'undefined' && (data.data as any as string) === 'Unauthorized.') {
                        // Let retry() below reconnect, same as any other dropped connection, instead of forcing
                        // a logout: a stale in-flight request can still surface this after a valid token refresh.
                        throw new Error('Websocket unauthorized');
                    } else if (!data.success) {
                        this.onError(data.data);

                        return undefined;
                    }

                    return data;
                }),
                retry({
                    count: 20,
                    resetOnSuccess: true,
                    delay: (error: unknown, retryAttempt: number) => {
                        const retryScalingBackoff = 500;

                        if (error instanceof HttpErrorResponse && error.status === 500) {
                            return throwError(() => error);
                        }

                        console.warn(
                            `Websocket reconnect attempt ${retryAttempt}: retrying in ${retryAttempt * retryScalingBackoff}ms`
                        );

                        return timer(retryAttempt * retryScalingBackoff);
                    },
                }),
                catchError(() => NEVER),
                share()
            );
        }

        return this.sharedWebsocket;
    }

    public doRequest(type: string, fromDate?: Date, toDate?: Date, singleId?: number | string) {
        this.getFullWebsocket().next({ type, fromDate, toDate, singleId, access_token: LocalStorage.getUserToken() });
    }

    private getFullWebsocket() {
        if (this.websocket) {
            return this.websocket;
        }

        this.websocket = webSocket({
            url: `${environment.wsEndpoint}?access_token=${LocalStorage.getUserToken()}`,
            openObserver: {
                next: () => {
                    this.websocketOpen.emit(true);
                },
            },
            closeObserver: {
                next: () => {
                    this.websocketOpen.emit(false);
                },
            },
        });

        return this.websocket;
    }

    private onError(error: unknown): ApiErrorResponse {
        const err = new ApiErrorResponse();

        const errorMessage = error instanceof Error ? error.message : typeof error === 'string' ? error : undefined;

        err.success = false;

        this.dialog.closeAll();

        this.confirmDialogService.confirm({
            title: 'Er is een fout opgetreden',
            text: errorMessage,
            confirm: 'Sluiten',
        });

        return err;
    }
}
